// Web-exclusive Job Description screen - Full job details
// Phase 3: Task 3.7

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput } from 'react-native';
import { useSelector } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import WebLayout from '../../components/web/WebLayout';
import WebModal from '../../components/web/WebModal';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getJob, applyJob, getAllJobs } from '../../services/job';
import { useIsMobile } from '../../hooks/useIsMobile';

const JobDescriptionScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user);
  const { jobId } = route.params || {};
  const isMobile = useIsMobile();

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [applySuccess, setApplySuccess] = useState(false);

  // Fetch job details
  const { data: jobData, isLoading, error } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob({ uuid: jobId }),
    enabled: !!jobId,
  });
  const job = jobData?.data;

  // Fetch similar jobs
  const { data: similarJobsData } = useQuery({
    queryKey: ['similarJobs', job?.profession_id],
    queryFn: () => getAllJobs({ queryKey: ['jobs', { profession_id: job?.profession_id, limit: 3 }] }),
    enabled: !!job?.profession_id,
  });
  const similarJobs = similarJobsData?.data?.filter((j) => j.uuid !== jobId)?.slice(0, 3) || [];

  // Apply mutation
  const applyMutation = useMutation({
    mutationFn: applyJob,
    onSuccess: () => {
      setApplySuccess(true);
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['job', jobId]);
    },
  });

  const handleApply = () => {
    applyMutation.mutate({
      job_uuid: jobId,
      cover_letter: coverLetter,
    });
  };

  const formatSalary = (min, max, currency = 'RM') => {
    if (!min && !max) return 'Competitive';
    if (min && max) return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    if (min) return `From ${currency} ${min.toLocaleString()}`;
    return `Up to ${currency} ${max.toLocaleString()}`;
  };

  if (isLoading) {
    return (
      <WebLayout>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading job details...</Text>
        </View>
      </WebLayout>
    );
  }

  if (error || !job) {
    return (
      <WebLayout>
        <View style={styles.errorContainer}>
          <Text style={styles.errorIcon}>❌</Text>
          <Text style={styles.errorTitle}>Job not found</Text>
          <Text style={styles.errorText}>This job may have been removed or the link is invalid.</Text>
          <TouchableOpacity style={styles.backToJobsBtn} onPress={() => navigation.navigate('Jobs')}>
            <Text style={styles.backToJobsBtnText}>Browse All Jobs</Text>
          </TouchableOpacity>
        </View>
      </WebLayout>
    );
  }

  return (
    <WebLayout>
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Breadcrumb */}
        <View style={styles.breadcrumb}>
          <TouchableOpacity onPress={() => navigation.navigate('Jobs')}>
            <Text style={styles.breadcrumbLink}>Jobs</Text>
          </TouchableOpacity>
          <Text style={styles.breadcrumbSeparator}>/</Text>
          <Text style={styles.breadcrumbCurrent}>{job.title}</Text>
        </View>

        <View style={[styles.contentLayout, isMobile && styles.contentLayoutMobile]}>
          {/* Main Content */}
          <View style={styles.mainContent}>
            {/* Job Header Card */}
            <View style={styles.jobHeaderCard}>
              <View style={styles.jobHeaderTop}>
                <View style={[styles.companyLogo, { backgroundColor: getColorFromName(job.clinic_name) }]}>
                  <Text style={styles.companyLogoText}>{job.clinic_name?.[0] || 'C'}</Text>
                </View>
                <View style={styles.jobHeaderInfo}>
                  <Text style={[styles.jobTitle, isMobile && styles.jobTitleMobile]}>{job.title}</Text>
                  <Text style={styles.companyName}>{job.clinic_name || 'Company'}</Text>
                  <View style={styles.jobMeta}>
                    <View style={styles.metaItem}>
                      <Text style={styles.metaIcon}>📍</Text>
                      <Text style={styles.metaText}>{job.location || job.city || 'Location'}</Text>
                    </View>
                    <View style={styles.metaItem}>
                      <Text style={styles.metaIcon}>💰</Text>
                      <Text style={styles.metaText}>
                        {formatSalary(job.salary_min, job.salary_max)}
                        {job.salary_type ? ` / ${job.salary_type}` : ''}
                      </Text>
                    </View>
                    <View style={styles.metaItem}>
                      <Text style={styles.metaIcon}>📅</Text>
                      <Text style={styles.metaText}>
                        Posted {formatDate(job.created_at)}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
              <View style={styles.jobTags}>
                <View style={styles.jobTypeTag}>
                  <Text style={styles.jobTypeTagText}>{job.type || 'Full-time'}</Text>
                </View>
                {job.is_urgent && (
                  <View style={styles.urgentTag}>
                    <Text style={styles.urgentTagText}>Urgent</Text>
                  </View>
                )}
                {job.profession_name && (
                  <View style={styles.professionTag}>
                    <Text style={styles.professionTagText}>{job.profession_name}</Text>
                  </View>
                )}
              </View>
            </View>

            {/* Job Description */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Job Description</Text>
              <Text style={styles.descriptionText}>
                {job.description || 'No description provided.'}
              </Text>
            </View>

            {/* Requirements */}
            {job.requirements && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Requirements</Text>
                <View style={styles.listContainer}>
                  {parseListItems(job.requirements).map((item, index) => (
                    <View key={index} style={styles.listItem}>
                      <Text style={styles.listBullet}>•</Text>
                      <Text style={styles.listText}>{item}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Responsibilities */}
            {job.responsibilities && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Responsibilities</Text>
                <View style={styles.listContainer}>
                  {parseListItems(job.responsibilities).map((item, index) => (
                    <View key={index} style={styles.listItem}>
                      <Text style={styles.listBullet}>•</Text>
                      <Text style={styles.listText}>{item}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Benefits */}
            {job.benefits && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Benefits</Text>
                <View style={styles.benefitsGrid}>
                  {parseListItems(job.benefits).map((benefit, index) => (
                    <View key={index} style={[styles.benefitItem, isMobile && styles.benefitItemMobile]}>
                      <Text style={styles.benefitIcon}>✓</Text>
                      <Text style={styles.benefitText}>{benefit}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* About Company */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>About {job.clinic_name}</Text>
              <Text style={styles.descriptionText}>
                {job.company_description || `${job.clinic_name} is a healthcare facility providing quality medical services.`}
              </Text>
              {job.clinic_address && (
                <View style={styles.companyAddress}>
                  <Text style={styles.addressIcon}>📍</Text>
                  <Text style={styles.addressText}>{job.clinic_address}</Text>
                </View>
              )}
            </View>
          </View>

          {/* Sidebar */}
          <View style={[styles.sidebar, isMobile && styles.sidebarMobile]}>
            {/* Apply Card */}
            <View style={styles.applyCard}>
              <Text style={styles.applyCardTitle}>Interested in this job?</Text>
              {job.has_applied ? (
                <View style={styles.appliedBadge}>
                  <Text style={styles.appliedBadgeText}>✓ Already Applied</Text>
                </View>
              ) : user?.isDoctor ? (
                <TouchableOpacity
                  style={styles.applyBtn}
                  onPress={() => setShowApplyModal(true)}
                >
                  <Text style={styles.applyBtnText}>Apply Now</Text>
                </TouchableOpacity>
              ) : (
                <Text style={styles.facilityNote}>
                  Login as a healthcare professional to apply
                </Text>
              )}
              <TouchableOpacity style={styles.saveBtn}>
                <Text style={styles.saveBtnText}>⭐ Save Job</Text>
              </TouchableOpacity>
            </View>

            {/* Job Summary Card */}
            <View style={styles.summaryCard}>
              <Text style={styles.summaryTitle}>Job Summary</Text>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Job Type</Text>
                <Text style={styles.summaryValue}>{job.type || 'Full-time'}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Salary</Text>
                <Text style={styles.summaryValue}>
                  {formatSalary(job.salary_min, job.salary_max)}
                </Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Location</Text>
                <Text style={styles.summaryValue}>{job.location || job.city || '-'}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Experience</Text>
                <Text style={styles.summaryValue}>
                  {job.experience_years ? `${job.experience_years}+ years` : 'Not specified'}
                </Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Posted</Text>
                <Text style={styles.summaryValue}>{formatDate(job.created_at)}</Text>
              </View>
              {job.deadline && (
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}>Deadline</Text>
                  <Text style={styles.summaryValue}>
                    {new Date(job.deadline).toLocaleDateString()}
                  </Text>
                </View>
              )}
            </View>

            {/* Similar Jobs */}
            {similarJobs.length > 0 && (
              <View style={styles.similarCard}>
                <Text style={styles.similarTitle}>Similar Jobs</Text>
                {similarJobs.map((similarJob) => (
                  <TouchableOpacity
                    key={similarJob.uuid}
                    style={styles.similarJobItem}
                    onPress={() => navigation.push('JobDescription', { jobId: similarJob.uuid })}
                  >
                    <View style={[styles.similarJobLogo, { backgroundColor: getColorFromName(similarJob.clinic_name) }]}>
                      <Text style={styles.similarJobLogoText}>{similarJob.clinic_name?.[0]}</Text>
                    </View>
                    <View style={styles.similarJobInfo}>
                      <Text style={styles.similarJobTitle} numberOfLines={1}>{similarJob.title}</Text>
                      <Text style={styles.similarJobCompany}>{similarJob.clinic_name}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Apply Modal */}
      <WebModal
        visible={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setApplySuccess(false);
          setCoverLetter('');
        }}
        title={applySuccess ? 'Application Submitted!' : `Apply to ${job.title}`}
      >
        {applySuccess ? (
          <View style={styles.successContent}>
            <Text style={styles.successIcon}>✓</Text>
            <Text style={styles.successTitle}>Application Submitted!</Text>
            <Text style={styles.successText}>
              Your application has been sent to {job.clinic_name}. You'll be notified when they respond.
            </Text>
            <TouchableOpacity
              style={styles.successBtn}
              onPress={() => {
                setShowApplyModal(false);
                setApplySuccess(false);
                navigation.navigate('Inbox');
              }}
            >
              <Text style={styles.successBtnText}>View My Applications</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.applyModalContent}>
            <Text style={styles.applyModalLabel}>Cover Letter (Optional)</Text>
            <TextInput
              style={styles.coverLetterInput}
              multiline
              numberOfLines={6}
              value={coverLetter}
              onChangeText={setCoverLetter}
              placeholder="Tell the employer why you're a great fit for this role..."
              placeholderTextColor={webColors.textMuted}
            />
            <View style={styles.applyModalActions}>
              <TouchableOpacity
                style={styles.cancelModalBtn}
                onPress={() => setShowApplyModal(false)}
              >
                <Text style={styles.cancelModalBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.submitApplyBtn, applyMutation.isPending && styles.submitApplyBtnDisabled]}
                onPress={handleApply}
                disabled={applyMutation.isPending}
              >
                <Text style={styles.submitApplyBtnText}>
                  {applyMutation.isPending ? 'Submitting...' : 'Submit Application'}
                </Text>
              </TouchableOpacity>
            </View>
            {applyMutation.isError && (
              <Text style={styles.errorText}>
                {applyMutation.error?.response?.data?.message || 'Failed to submit application'}
              </Text>
            )}
          </View>
        )}
      </WebModal>
    </WebLayout>
  );
};

// Helper functions
const getColorFromName = (name) => {
  const colors = ['#fce7f3', '#dbeafe', '#dcfce7', '#fef3c7', '#f3e8ff'];
  return colors[name ? name.charCodeAt(0) % colors.length : 0];
};

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diff = now - d;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const parseListItems = (text) => {
  if (!text) return [];
  // Handle both newline-separated and bullet-point separated lists
  return text
    .split(/[\n•\-]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 100,
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 24,
  },
  backToJobsBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  backToJobsBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
  },
  breadcrumbLink: {
    fontSize: 14,
    color: webColors.primary,
    fontWeight: '500',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  breadcrumbCurrent: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  contentLayout: {
    flexDirection: 'row',
    gap: 24,
  },
  mainContent: {
    flex: 1,
  },
  sidebar: {
    width: 340,
    flexShrink: 0,
  },
  jobHeaderCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 24,
  },
  jobHeaderTop: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 20,
  },
  companyLogo: {
    width: 72,
    height: 72,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  companyLogoText: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  jobHeaderInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  companyName: {
    fontSize: 16,
    color: webColors.textLink,
    marginBottom: 12,
  },
  jobMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaIcon: {
    fontSize: 14,
  },
  metaText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  jobTags: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  jobTypeTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: webColors.primaryLight,
    borderRadius: webBorderRadius.small,
  },
  jobTypeTagText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
    textTransform: 'capitalize',
  },
  urgentTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#fee2e2',
    borderRadius: webBorderRadius.small,
  },
  urgentTagText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#dc2626',
  },
  professionTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
  },
  professionTagText: {
    fontSize: 13,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  section: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
  },
  descriptionText: {
    fontSize: 15,
    color: webColors.textSecondary,
    lineHeight: 24,
  },
  listContainer: {},
  listItem: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  listBullet: {
    fontSize: 16,
    color: webColors.primary,
    lineHeight: 24,
  },
  listText: {
    flex: 1,
    fontSize: 15,
    color: webColors.textSecondary,
    lineHeight: 24,
  },
  benefitsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: 'calc(50% - 6px)',
    padding: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
  },
  benefitIcon: {
    fontSize: 14,
    color: webColors.success,
  },
  benefitText: {
    fontSize: 14,
    color: webColors.textSecondary,
    flex: 1,
  },
  companyAddress: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
    padding: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
  },
  addressIcon: {
    fontSize: 14,
  },
  addressText: {
    fontSize: 14,
    color: webColors.textMuted,
    flex: 1,
  },
  applyCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 16,
  },
  applyCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 16,
  },
  applyBtn: {
    width: '100%',
    paddingVertical: 14,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    marginBottom: 12,
    ...webShadows.button,
  },
  applyBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  appliedBadge: {
    width: '100%',
    paddingVertical: 14,
    backgroundColor: '#dcfce7',
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    marginBottom: 12,
  },
  appliedBadgeText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#16a34a',
  },
  facilityNote: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
    marginBottom: 12,
  },
  saveBtn: {
    width: '100%',
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  summaryCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 16,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  summaryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  summaryLabel: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
    textAlign: 'right',
    maxWidth: '60%',
  },
  similarCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
  },
  similarTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
  },
  similarJobItem: {
    flexDirection: 'row',
    gap: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  similarJobLogo: {
    width: 40,
    height: 40,
    borderRadius: webBorderRadius.small,
    alignItems: 'center',
    justifyContent: 'center',
  },
  similarJobLogoText: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  similarJobInfo: {
    flex: 1,
  },
  similarJobTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  similarJobCompany: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  applyModalContent: {
    padding: 24,
  },
  applyModalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  coverLetterInput: {
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
    color: webColors.textPrimary,
    backgroundColor: webColors.background,
    minHeight: 150,
    textAlignVertical: 'top',
    marginBottom: 20,
  },
  applyModalActions: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
  },
  cancelModalBtn: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
  },
  cancelModalBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  submitApplyBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  submitApplyBtnDisabled: {
    opacity: 0.6,
  },
  submitApplyBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  successContent: {
    padding: 40,
    alignItems: 'center',
  },
  successIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#dcfce7',
    fontSize: 28,
    lineHeight: 64,
    textAlign: 'center',
    color: '#16a34a',
    marginBottom: 20,
  },
  successTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  successText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
    marginBottom: 24,
  },
  successBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  successBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  contentLayoutMobile: {
    flexDirection: 'column',
  },
  sidebarMobile: {
    width: '100%',
  },
  benefitItemMobile: {
    width: '100%',
  },
  jobTitleMobile: {
    fontSize: 20,
  },
});

export default JobDescriptionScreen;
